import { Injectable } from '@angular/core';
import { Big } from 'big.js';
import {
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';
import { parseAmount } from '../../../subscription-form.utils';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = {
      ...option,
    };
    let options: AbstractControlOptions = {};

    let supportRepartition: UntypedFormGroup[] = [];

    const entitySupportRepartition =
      entity?.parameters?.currentSupportRepartition?.supportRepartition || [];

    if (entitySupportRepartition)
      _.forEach(entitySupportRepartition, (support, i) =>
        supportRepartition.push(
          this.formBuilder.group(this.createModel(support)),
        ),
      );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        totalAmount: [entity?.parameters?.lastRecordAmount || null],
        supportRepartition: this.formBuilder.array(
          supportRepartition,
          Validators.required,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }
  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  public createModel(item) {
    return {
      supportId: [item ? item.supportId : null],
      supportName: [item ? item.supportName : null],
      isin: [item ? item.isin : null],
      isr: [item ? item.isr : null],
      amount: [
        item && !_.isNil(item.amount) ? item.amount : 0,
        [Validators.required, Validators.min(0)],
      ],
      percentage: [item ? item.percentage : null],
      supportType: [item ? item.supportType : null],
      index: [item?.index || null],
      perfP3Y: [item?.perfP3Y || null],
      volat3Y: [item?.volat3Y || null],
      risk: [item?.risk || null],
      // PRE-4896 : cette slide n'affiche ni ne modifie ces 8 champs, mais doit les faire transiter
      // tels quels (préremplis par la commande COMPLETE_SUPPORT_RATES au submit de cette slide) —
      // sans cela, l'extract() écraserait leur valeur à null avant même que la slide
      // "tauxParSupportTransfer" ait pu les afficher.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      upfrontAssureurSaisi: [item?.upfrontAssureurSaisi ?? null],
      upfrontEmetteurSaisi: [item?.upfrontEmetteurSaisi ?? null],
      fraisGestionSaisi: [item?.fraisGestionSaisi ?? null],
      retrocessionAssureurSaisi: [item?.retrocessionAssureurSaisi ?? null],
    };
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }

  get totalAmount() {
    return this.products.get(`${0}`).get('parameters').get('totalAmount').value;
  }

  get productId() {
    return this.form.get('products').get(`${0}`).get('productId').value;
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      let supportRepartition = _.cloneDeep(
        product.parameters.supportRepartition,
      );
      let totalAmount = supportRepartition
        .reduce(
          (acc, item) => acc.plus(new Big(parseAmount(item?.amount))),
          new Big(0),
        )
        .toNumber();
      product.parameters.totalAmount = undefined;
      product.parameters.currentSupportRepartition = {
        totalAmount: totalAmount,
        supportRepartition: supportRepartition,
      };
    });
    return products;
  }
}
